import React, { FC, useState, useEffect, ReactNode, useRef } from 'react'
import type { PaginationProps } from 'antd'
import type { FilterConfirmProps } from 'antd/es/table/interface'
import { Space, Table, Typography, Modal, notification, Form, InputNumber, Button, Select, Input } from 'antd'
import { CloseCircleOutlined, CheckCircleOutlined, FormOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageBreadcrumb from '@/components/pageBreadcrumb'
import NoSpaceInput from '@/components/noSpaceInput'
import type { ColumnType } from 'antd/es/table'
import Highlighter from 'react-highlight-words'
import type { IQueryParam } from '@/services/progress.service'
import { addProd, delProd, getBUList, getProdList, modifyProd } from '@/services/progress.service'
import { getErrorStr } from '@/store/sagas/table.saga'
import { requestFail } from '@/store/actions/common.action'
import { IBu, IProduct } from '@/store/models/auth.model'
import _ from 'lodash'
import Nav from '@/components/nav'

interface IColProperty {
    title?: ReactNode | string
    dataIndex?: string
    editable?: boolean
    ellipsis?: { showTitle: boolean },
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    render?: (_: any, record: any) => ReactNode
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    onCell?: (record: IProduct) => any
    filterDropdown?: any
    filterDropdownOpen?: boolean
}
interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
    editing: boolean
    dataIndex: string
    inputType: 'number' | 'text' | 'select'
    title: string
    record: IProduct
    index: number
    children: ReactNode
}
type IDataIndex = keyof IProduct

const ProdListMana: FC = () => {
    const history = useHistory()
    const dispatch = useDispatch()
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const locationObj = useLocation()
    const { t, i18n } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [winH, setWinh] = useState(0)
    const [buList, setBuList] = useState<IBu[]>([])
    const [tableData, setTableData] = useState<IProduct[]>([])
    const [editingKey, setEditingKey] = useState('')
    const [searchText, setSearchText] = useState<string | null>(null)
    // const [filterDropdownOpen, setFilterDropdownOpen] = useState(false)
    const [searchedColumn, setSearchedColumn] = useState('')
    const [searchCont, setSearchCont] = useState({
        classification: '',
        platform: ''
    })
    const [paginationInfo, setPaginationInfo] = useState({
        pageIndex: 1,
        pageSize: 10,
        total: 0
    })
    // pageSize 变更后，会重复触发 pageChange 事件
    const pageSizeChanged = useRef<boolean>(false)
    // const searchInput = useRef<InputRef>(null)
    const [form] = Form.useForm()
    const [modalVisible, setModalVisible] = useState(false)
    const [modalTitle, setModalTitle] = useState('')
    const [addForm] = Form.useForm()
    
    const { Text } = Typography

    const isEditing = (record: IProduct) => ('' + record.id) === editingKey

    const popMsg = (msg: string) => {
        const args = {
            message: msg,
            description: '',
            icon: <CheckCircleOutlined style={{ color: '#08993f' }} />,
            duration: 3,
        }
        notification.open(args)
    }
    
    const EditableCell: React.FC<EditableCellProps> = ({
        editing,
        dataIndex,
        inputType,
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        title,
        children,
        ...restProps
    }) => {
        let inputNode: ReactNode
        switch (inputType) {
            case 'number':
                inputNode = <InputNumber />
                break
            case 'select':
                inputNode = <Select placeholder={t('Please Select') + (i18n.language !== 'zh' ? ' ' : '') + t('buAndCtList')}
                    allowClear
                    mode="multiple"
                    showArrow
                    options={buList.map(item => ({ value: item.id, label: item.bu }))}
                />
                break
            default:
                inputNode = <NoSpaceInput maxLength={20} />
                break
        }
        return (
            <td {...restProps}>
                {editing ? (
                    <Form.Item
                        name={dataIndex !== 'bus' ? dataIndex : 'buIds'}
                        style={{ margin: 0 }}
                        rules={[
                            {
                                required: true,
                                message: inputType === 'select'
                                    ? t('Please Select') + (i18n.language !== 'zh' ? ' ' : '') + t('buAndCtList')
                                    : t('Please Enter') + (i18n.language !== 'zh' ? ' ' : '') + t('content'),
                            },
                        ]}
                    >
                        {inputNode}
                    </Form.Item>
                ) : (
                    children
                )}
            </td>
        )
    }
    
    const handleSearch = (
        selectedKeys: string[],
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: IDataIndex,
    ) => {
        setSearchText(selectedKeys[0])
        setSearchedColumn(dataIndex)
        const params: IQueryParam = {
            pageIndex: 1,
            pageSize: paginationInfo.pageSize
        }
        if (dataIndex === 'platform') {
            params.platform = selectedKeys[0] || ''
        } else if (dataIndex === 'platformClassification') {
            params.classification = selectedKeys[0] || ''
        }
        // 避免重复触发 pageChange 事件
        pageSizeChanged.current = true
        setTimeout(() => {
            pageSizeChanged.current = false
        }, 100)
        getList(params)
        confirm()
    }
    const handleReset = (clearFilters: () => void) => {
        clearFilters()
        setSearchText('')
    }
    const getColumnSearchProps = (dataIndex: IDataIndex): ColumnType<any> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (<>
            <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()}>
                <NoSpaceInput
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(val: string) => {
                        setSelectedKeys(val ? [val] : [])
                    }}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >Search</Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >Reset</Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm()
                        }}
                    >close</Button>
                </Space>
            </div>
        </>),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#fff' : undefined }} />
        ),
        render: text => searchedColumn === dataIndex ? (
            <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={searchText ? [searchText] : []}
                autoEscape
                textToHighlight={text ? text.toString() : ''}
            />
        ) : text,
        // filterDropdownVisible: filterDropdownOpen,
        // onFilterDropdownVisibleChange: visible => {
        //     setFilterDropdownOpen(visible)
        //     if (visible) {
        //         setFilterDropdownOpen(true)
        //         setTimeout(() => searchInput.current?.select(), 100)
        //     }
        // }
    })
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const viewButton = (_: any, record: any) => {
        const editable = isEditing(record)
        return !editable ? (
            <Space size={20}>
                <Text className={`${editingKey === '' ? 'txt_blue hand' : ''}`} title={t('edit')} onClick={() => {
                    if (editingKey === '') {
                        setEditingKey('' + record.id)
                        edit(record)
                    }
                }}><FormOutlined style={{ fontSize: 16 }} /></Text>
                <Text className={`${editingKey === '' ? 'txt_red hand' : ''}`} title={t('delete')} onClick={() => {
                    if (editingKey === '') {
                        delFun(record.id)
                    }
                }}><DeleteOutlined style={{ fontSize: 16 }} /></Text>
            </Space>
        ) : (
            <Space size={20}>
                <Button type="text" htmlType="submit" className='btn_txt'>
                    <Text className='txt_blue hand' title={t('save')}><CheckCircleOutlined style={{ fontSize: 16 }} /></Text>
                </Button>
                <Text className='txt_red hand' title={t('cancel')} onClick={cancel}><CloseCircleOutlined style={{ fontSize: 16 }} /></Text>
            </Space>
        )
    }
    const columns: any[] = [
        {
            title: () => <span>{t('product') + (i18n.language !== 'zh' ? ' ' : '') + t('Classification')}</span>,
            dataIndex: 'platform',
            key: 'platform',
            editable: true,
            width: 250,
            ...getColumnSearchProps('platform'),
        },
        {
            title: () => <span>{t('productName')}</span>,
            dataIndex: 'platformClassification',
            key: 'platformClassification',
            editable: true,
            width: 150,
            ...getColumnSearchProps('platformClassification'),
        },
        {
            title: () => <span>{t('product') + (i18n.language !== 'zh' ? ' ' : '') + t('platform')}</span>,
            dataIndex: 'productPlatform',
            key: 'productPlatform',
            width: 250,
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('buAndCtList')}</span>,
            dataIndex: 'bus',
            key: 'bus',
            editable: true,
            width: 250,
            ellipsis: { showTitle: true },
            render(value: any[]) {
                return value.map((item: any) => item.bu)?.join(', ') || ''
            }
        },
        {
            title: () => <span>{t('Action')}</span>,
            dataIndex: 'operation',
            key: 'operation',
            width: 150,
            fixed: 'right',
            render: viewButton
        },
    ]

    const mergedColumns = columns.map((col: any): IColProperty => {
        if (!col.editable) {
            return col
        }
        return {
            ...col,
            onCell: (record: IProduct) => ({
                record,
                inputType: col.dataIndex === 'bus' ? 'select' : 'text',
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
            }),
        }
    })
    const addFun = () => {
        setModalTitle(t('add'))
        setModalVisible(true)
        addForm.resetFields()
    }
    const handleCancel = () => {
        setModalVisible(false)
        addForm.resetFields()
    }
    const handleAdd = async (values: any) => {
        try {
            await addProd(values)
            setModalVisible(false)
            addForm.resetFields()   
            getList({
                pageIndex: 1,
                pageSize: paginationInfo.pageSize,
            })
            popMsg(t('save') + (i18n.language !== 'zh' ? ' ' : '') + t('success'))
        } catch (error: any) {
            const str = getErrorStr(error)
            dispatch(requestFail(str))
        }
    }
    const edit = (record: Partial<any>) => {
        console.log('record: ', record)
        form.setFieldsValue({ ...record, buIds: record.bus.map((item: any) => item.id) })
        setEditingKey('' + record.id)
    }
    const cancel = () => {
        setEditingKey('')
        const arr = tableData.filter(item => ('' + item.id)?.length !== 8)
        setTableData(arr)
    }
    const save = async (record: any) => {
        try {
            const newData = _.cloneDeep(tableData)
            const findIndex = newData.findIndex(item => ('' + item.id) === ('' + record.id))
            const row = await form.validateFields()
            if (record.id.length === 8) {
                const resId = await addProd(record)
                record.id = resId
            } else {
                await modifyProd({
                    ...row,
                    id: '' + record.id
                })
            }
            setEditingKey('')
            if (findIndex !== -1) {
                const bus = buList.filter(item => row.buIds.includes(item.id))
                newData.splice(findIndex, 1, {
                    ...record,
                    platform: row.platform,
                    platformClassification: row.platformClassification,
                    bus
                })
                setTableData(newData)
            }
            getList({
                pageIndex: 1,
                pageSize: paginationInfo.pageSize,
            })
            popMsg(t('save') + (i18n.language !== 'zh' ? ' ' : '') + t('success'))
        } catch (error: any) {
            const str = getErrorStr(error)
            dispatch(requestFail(str))
        }
    }
    const delFun = async (id: number) => {
        Modal.confirm({
            title: t('confirmDel'),
            centered: true,
            okText: t('confirm'),
            cancelText: t('cancel'),
            async onOk () {
                try {
                    await delProd('' + id)
                    popMsg(t('delete') + (i18n.language !== 'zh' ? ' ' : '') + t('success'))
                    setEditingKey('')
                    getList({
                        pageIndex: paginationInfo.pageIndex,
                        pageSize: paginationInfo.pageSize,
                    })
                } catch (error: any) {
                    const str = getErrorStr(error)
                    dispatch(requestFail(str))
                }
            }
        })
    }

    // 分页
    const onPaginationChange: PaginationProps['onChange'] = (pageNumber: number): void => {
        if (pageSizeChanged.current) return
        console.log('pageNumber: ', pageNumber)
        const pagination = {
            ...paginationInfo,
            classification: searchCont?.classification || '',
            platform: searchCont?.platform || '',
            pageIndex: pageNumber
        }
        getList(pagination)
    }
    const onPageSizeChange = (current: number, size: number) => {
        // 避免重复触发 pageChange 事件
        pageSizeChanged.current = true
        const pagination = {
            ...paginationInfo,
            classification: searchCont?.classification || '',
            platform: searchCont?.platform || '',
            pageIndex: 1,
            pageSize: size
        }
        getList(pagination)
        setTimeout(() => {
            pageSizeChanged.current = false
        }, 100)
    }

    const onFinish = (values: IProduct) => {
        const record = tableData.find(item => item.id === Number(editingKey))
        save({
            ...record,
            ...values,
            id: editingKey
        })
    }

    const getBUListFun = async () => {
        try {
            setLoading(true)
            const res = await getBUList().catch(() => {
                setLoading(false)
            })
            setBuList(res || [])
        } catch (error: any) {
            const str = getErrorStr(error)
            dispatch(requestFail(str))
        }
    }
    const getList = async (params: IQueryParam) => {
        console.log('params: ', params)
        try {
            setLoading(true)
            const res = await getProdList({
                classification: params?.classification || '',
                platform: params?.platform || '',
                pageNo: params.pageIndex,
                pageSize: params.pageSize,
            }).finally(() => {
                setLoading(false)
            })
            setSearchCont({
                classification: params?.classification || '',
                platform: params?.platform || ''
            })
            setPaginationInfo({
                pageIndex: params?.pageIndex ? params.pageIndex : 1,
                pageSize: params?.pageSize ? params.pageSize : 10,
                total: res.totalElements
            })
            setTableData(res.content)
        } catch (error: any) {
            const str = getErrorStr(error)
            dispatch(requestFail(str))
        }
    }

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const [routes, setRoutes] = useState([
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        {
            path: '/prodListMana',
            breadcrumbName: t('product') + (i18n.language !== 'zh' ? ' ' : '') + t('inventory')
                + (i18n.language !== 'zh' ? ' ' : '') + t('manage'),
        },
    ])
    useEffect(() => {
        getBUListFun()
        getList({
            pageSize: 10,
            pageIndex: 1
        })

        const h = 295
        setWinh(document.body.clientHeight - h)
        window.onresize = () => {
            setWinh(document.body.clientHeight - h)
        }
        return () => {
            window.onresize = null
        }
    }, [])
    return (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Nav dispatch={dispatch} />
            <PageBreadcrumb routerParam={routes}
                history={history}
                dispatch={dispatch}
            />
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
                <Button type='primary'
                    style={{ margin: '0 25px' }}
                    onClick={addFun}
                    disabled={editingKey !== ''}
                >{t('add')}</Button>
                <Form form={form} onFinish={onFinish}>
                    <Table bordered
                        className='index_ls'
                        components={{
                            body: {
                                cell: EditableCell,
                            },
                        }}
                        dataSource={tableData}
                        loading={loading}
                        columns={mergedColumns}
                        rowKey="id"
                        scroll={{ x: 'max-content', y: winH }}
                        pagination={{
                            current: paginationInfo.pageIndex,
                            pageSize: paginationInfo.pageSize,
                            total: paginationInfo.total,
                            showQuickJumper: true,
                            onChange: onPaginationChange,
                            showSizeChanger: true,
                            onShowSizeChange: onPageSizeChange,
                            showTotal: (total) => 
                                `${t('total')} ${total} ${t('items')}`,
                            style: { paddingBottom: 10 }
                        }}
                    />
                </Form>
            </Space>
            <Modal
                title={modalTitle}
                visible={modalVisible}
                onCancel={handleCancel}
                footer={null}
                destroyOnClose
            >
                <Form form={addForm} onFinish={handleAdd}>
                    <Form.Item
                        name="platform"
                        label={t('product') + (i18n.language !== 'zh' ? ' ' : '') + t('Classification')}
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="platformClassification"
                        label={t('productName')}
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="buIds"
                        label={t('buAndCtList')}
                        rules={[{ required: true }]}
                    >
                        <Select mode="multiple">
                            {buList.map(item => (
                                <Select.Option key={item.id} value={item.id}>{item.bu}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item style={{ textAlign: 'center' }}>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {t('submit')}
                            </Button>
                            <Button onClick={handleCancel}>
                                {t('cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    )
}

export default ProdListMana